import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";
import { LIQUID_EASE } from "../utils/liquidMotion";

/**
 * NotificationsProvider
 *
 * Usage:
 *  - Wrap your app once (you already did in index.js)
 *  - In components: const { notify } = useNotification();
 *  - notify("Saved", "success", { autoDismiss: 2000, fullscreen: false });
 *
 * Options:
 *  - autoDismiss: ms (false/0 = do not auto dismiss)
 *  - fullscreen: boolean -> true for full page overlay (blur background)
 *  - sound: boolean -> play sound
 *  - icon: react node (optional)
 */

const NotificationsContext = createContext(null);

let portalRoot = null;
function ensurePortalRoot() {
  if (typeof document === "undefined") return null;
  if (!portalRoot) {
    portalRoot = document.getElementById("global-notification-root");
    if (!portalRoot) {
      portalRoot = document.createElement("div");
      portalRoot.id = "global-notification-root";
      // extremely high z-index to keep above any header (but avoid overflow issues)
      portalRoot.style.zIndex = String(2147483645); // large but avoid overflow on some browsers
      portalRoot.style.position = "fixed";
      portalRoot.style.inset = "0";
      portalRoot.style.pointerEvents = "none"; // default none; notifications themselves enable pointer-events
      document.body.appendChild(portalRoot);
      // debug log (remove if noisy)
      // console.info("[NotificationsProvider] created portal root", portalRoot.id);
    }
  }
  return portalRoot;
}

// subtle webaudio sound (short)
const playBeep = (type = "info") => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    // different pitch for types
    const pitchMap = { success: 660, error: 220, warn: 330, info: 440 };
    o.frequency.value = pitchMap[type] || 440;
    g.gain.value = 0;
    o.connect(g);
    g.connect(ctx.destination);
    const now = ctx.currentTime;
    o.start(now);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    o.stop(now + 0.2);
    setTimeout(() => {
      try { ctx.close(); } catch {}
    }, 600);
  } catch (err) {
    // ignore audio errors silently
  }
};

const DEFAULT_AUTO_DISMISS = 3500;

function NotificationCard({ n, onClose }) {
  const { theme } = useTheme() || {};
  const isDtao = theme === "dtao";

  // choose animation & color per type
  const type = n.type || "info";
  const colors = {
    success: { accent: "#10B981" },
    error: { accent: "#EF4444" },
    warn: { accent: "#F59E0B" },
    info: { accent: "#3B82F6" },
    default: { accent: "#64748B" },
  };
  const style = colors[type] || colors.default;

  // "Water drop" entrance: falls in slightly elongated (like a drop
  // stretching as it falls), impacts and squashes wide on landing, then
  // settles with a small wobble — instead of a plain fade/scale-in.
  const fallDistance = n.fullscreen ? -70 : -46;
  const cardVariants = {
    initial: { y: fallDistance, opacity: 0, scaleY: 1.5, scaleX: 0.72 },
    animate: {
      y: [null, 5, -2, 0],
      opacity: [null, 1, 1, 1],
      scaleY: [null, 0.8, 1.05, 1],
      scaleX: [null, 1.2, 0.96, 1],
      transition: { duration: 0.68, times: [0, 0.5, 0.78, 1], ease: LIQUID_EASE },
    },
    exit: {
      y: n.fullscreen ? 24 : -10,
      opacity: 0,
      scaleY: 0.88,
      scaleX: 1.06,
      transition: { duration: 0.3, ease: [0.4, 0, 1, 1] },
    },
  };

  // Faint ripple ring behind the card, timed to expand right as it "lands".
  const rippleVariants = {
    initial: { opacity: 0, scale: 0.3 },
    animate: {
      opacity: [0, 0.35, 0],
      scale: [0.3, 1, 1.3],
      transition: { duration: 0.6, delay: 0.32, times: [0, 0.4, 1], ease: "easeOut" },
    },
  };

  const cardStyle = {
    pointerEvents: "auto",
    background: isDtao ? "rgba(18,10,26,0.94)" : "rgba(255,255,255,0.98)",
    borderRadius: n.fullscreen ? 14 : 10,
    boxShadow: isDtao ? "0 10px 40px rgba(0,0,0,0.5)" : "0 10px 40px rgba(15,23,42,0.18)",
    padding: n.fullscreen ? "18px 26px" : "10px 14px",
    minWidth: n.fullscreen ? "min(1200px, 86vw)" : 320,
    maxWidth: "min(1200px, 92vw)",
    display: "flex",
    alignItems: "center",
    gap: 14,
    border: isDtao ? "1px solid rgba(139,92,246,0.35)" : "1px solid rgba(0,0,0,0.06)",
  };

  const textStyle = { margin: 0, color: isDtao ? "#F1F5F9" : "#111827", fontWeight: 600, fontSize: n.fullscreen ? 18 : 14, textAlign: "center", lineHeight: 1.1 };

  return (
    <div style={{ position: "relative" }}>
      {/* impact ripple, centered behind the card, expanding outward on "landing" */}
      <motion.div
        initial="initial"
        animate="animate"
        variants={rippleVariants}
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: cardStyle.borderRadius,
          background: style.accent,
          pointerEvents: "none",
        }}
      />
      <motion.div
        layout
        variants={cardVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={cardStyle}
        className="toast-glow" // subtle CSS glow class (add CSS below)
        role="status"
        aria-live={type === "error" ? "assertive" : "polite"}
      >
        {/* accent bar / icon */}
        <div style={{ width: 10, height: 40, borderRadius: 8, background: style.accent }} aria-hidden />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={textStyle}>{n.message}</div>
          {n.detail ? <div style={{ marginTop: 6, fontSize: 13, color: isDtao ? "#A1A1AA" : "#6B7280", fontWeight: 500 }}>{n.detail}</div> : null}
        </div>

        {/* close button */}
        <button
          onClick={() => onClose(n.id)}
          aria-label="Close notification"
          style={{
            background: "transparent",
            border: 0,
            color: isDtao ? "#CBD5E1" : "#374151",
            cursor: "pointer",
            fontSize: 20,
            padding: 8,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </motion.div>
    </div>
  );
}

// quick adaptive timing for mobile vs desktop (UI-only helper)
const isMobileDevice = typeof window !== "undefined" && window.innerWidth < 768;

/**
 * Owns its own auto-dismiss timer so multiple stacked toasts can each count
 * down independently, instead of sharing one timer keyed on a single "current".
 */
function TimedToast({ n, onDone, onClose }) {
  useEffect(() => {
    if (n.sound) playBeep(n.type);

    if (n.autoDismiss !== false && n.autoDismiss > 0) {
      const t = setTimeout(() => onDone(n.id), n.autoDismiss || DEFAULT_AUTO_DISMISS);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n.id]);

  return <NotificationCard n={n} onClose={onClose} />;
}

export function NotificationsProvider({ children }) {
  const [items, setItems] = useState([]); // stacked, non-fullscreen toasts — multiple can be visible at once
  const [fsQueue, setFsQueue] = useState([]); // fullscreen notifications queue one at a time (blocking overlay)
  const [fsCurrent, setFsCurrent] = useState(null);
  const idRef = useRef(1);
  const portal = useMemo(() => ensurePortalRoot(), []);

  // show next queued fullscreen notification once the current one clears
  useEffect(() => {
    if (!fsCurrent && fsQueue.length > 0) {
      setFsCurrent(fsQueue[0]);
      setFsQueue((q) => q.slice(1));
    }
  }, [fsQueue, fsCurrent]);

  // fullscreen auto-dismiss + aria-hidden bookkeeping
  useEffect(() => {
    try {
      const root = document.getElementById("root");
      if (root) {
        if (fsCurrent) root.setAttribute("aria-hidden", "true");
        else root.removeAttribute("aria-hidden");
      }
    } catch {}

    if (!fsCurrent) return;
    if (fsCurrent.sound) playBeep(fsCurrent.type);

    const t = fsCurrent.autoDismiss !== false && fsCurrent.autoDismiss > 0
      ? setTimeout(() => {
          setFsCurrent((c) => (c && c.id === fsCurrent.id ? null : c));
        }, fsCurrent.autoDismiss || DEFAULT_AUTO_DISMISS)
      : null;

    return () => { if (t) clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fsCurrent]);

  // API function to push a notification
  const notify = (message = "", type = "info", opts = {}) => {
    const id = `n-${Date.now()}-${idRef.current++}`;
    const n = {
      id,
      message,
      type,
      detail: opts.detail || null,
      fullscreen: Boolean(opts.fullscreen),
      autoDismiss: typeof opts.autoDismiss === "number" ? opts.autoDismiss : (opts.fullscreen ? (opts.autoDismiss ?? 3500) : (opts.autoDismiss ?? 3000)),
      sound: Boolean(opts.sound),
      createdAt: Date.now(),
    };

    if (n.fullscreen) {
      if (!fsCurrent) setFsCurrent(n);
      else setFsQueue((q) => [...q, n]);
    } else {
      // Stacks alongside any other currently-visible toasts instead of queuing.
      setItems((prev) => [...prev, n]);
    }
    return id;
  };

  const dismissItem = (id) => setItems((prev) => prev.filter((x) => x.id !== id));

  const close = (id) => {
    if (fsCurrent && fsCurrent.id === id) {
      setFsCurrent(null);
      return;
    }
    if (fsQueue.some((x) => x.id === id)) {
      setFsQueue((q) => q.filter((x) => x.id !== id));
      return;
    }
    dismissItem(id);
  };

  const value = { notify, close };

  // Render portal UI
  const portalContent = (
    <div aria-hidden={false} style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {/* Fullscreen overlay — one at a time, blocking */}
      <AnimatePresence>
        {fsCurrent && (
          <motion.div
            key={fsCurrent.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: isMobileDevice ? 0.18 : 0.26 }}
            style={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 36,
              pointerEvents: "none",
              zIndex: 2147483646,
            }}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.25, 0.8, 0.3, 1] }}
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(15,23,42,0.25)",
                backdropFilter: "blur(8px) saturate(120%)",
                WebkitBackdropFilter: "blur(8px) saturate(120%)",
                pointerEvents: "auto",
              }}
              onClick={() => close(fsCurrent.id)}
            />
            <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
              <div style={{ pointerEvents: "auto" }}>
                <TimedToast n={fsCurrent} onDone={close} onClose={close} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stacked toasts — multiple can be visible at once, newest at the bottom of the stack */}
      <div
        style={{
          position: "fixed",
          top: 16,
          left: 0,
          right: 0,
          zIndex: 2147483646,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 10,
          pointerEvents: "none",
          padding: "0 16px",
        }}
      >
        <AnimatePresence mode="popLayout">
          {items.map((n) => (
            <motion.div key={n.id} layout style={{ pointerEvents: "auto" }}>
              <TimedToast n={n} onDone={dismissItem} onClose={dismissItem} />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );

  useEffect(() => {
    // ensure portal exists on mount
    ensurePortalRoot();
  }, []);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      {portal ? createPortal(portalContent, portal) : null}
    </NotificationsContext.Provider>
  );
}

export function useNotification() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error("useNotification must be used inside NotificationsProvider");
  }
  return ctx;
}
